import { useMemo } from 'react'

import GearIcon from '@/CustomIcons/GearIcon'
import { classifyAudioInputs } from '@/Logic'
import { ControlButton } from '@/UIComponents2/ControlButton'
import { SimpleSelect } from '@/UIComponents2/Select/SimpleSelect'
import { useUserContext } from '@/UserContext'
import MicrophoneIcon from '@/CustomIcons/MicrophoneIcon'
import { VolumeMeter } from './VolumeMeter'
import useMediaSelectionActions from '@/actions/useMediaSelectionActions'
import { cn } from '@/utlis/cn'

type TalkbackMicSelectProps = {
    onOpenSettings?: () => void
    multiple?: boolean
    className?: string
}
export function TalkbackMicSelect(props: TalkbackMicSelectProps) {
    const [userState] = useUserContext()
    const { toggleAudioDeviceSelection, setTalkbackDevice } = useMediaSelectionActions()

    const audioInputs = useMemo(() => {
        return classifyAudioInputs(userState.mediaSources.audioInputs).external.filter((inp) => inp.deviceId)
    }, [userState.mediaSources.audioInputs])

    const atrack = useMemo(() => {
        const currentlySelectedTalkbackMic = audioInputs.find(
            (externalDevice) => externalDevice.deviceId === userState.mediaSelection.talkbackMic
        )

        const currentSource = audioInputs.find((input) => input?.deviceId === currentlySelectedTalkbackMic?.deviceId)
        const currentSelection = currentSource
            ? userState.mediaSelection.audioInputs.find((i) => i.deviceId === currentSource.deviceId)
            : null
        const atrack = currentSelection
            ? userState.mediaStreams.audioInputs.find((i) => i.info.deviceId === currentSelection.deviceId)
            : null
        return atrack
    }, [audioInputs, userState])

    function handleTalkbackMicChange(deviceId: string) {
        const audioInput = userState.mediaSources.audioInputs.find((inp) => inp.deviceId === deviceId)

        const currentlySelectedTalkbackMic = audioInputs.find(
            (externalDevice) => externalDevice.deviceId === userState.mediaSelection.talkbackMic
        )

        // If the currently selected talkback mic is not the new target for talkback, toggle it off.
        if (currentlySelectedTalkbackMic && currentlySelectedTalkbackMic.deviceId !== audioInput?.deviceId) {
            toggleAudioDeviceSelection(currentlySelectedTalkbackMic)
        }

        const isInputAlreadyEnabled = userState.mediaSelection.audioInputs.find((input) => input.deviceId === deviceId)

        if (isInputAlreadyEnabled) {
            setTimeout(() => {
                setTalkbackDevice(deviceId)
            }, 0)
        } else if (audioInput) {
            setTimeout(() => {
                toggleAudioDeviceSelection(audioInput, { isTalkbackMic: true })
            }, 0)
        }
    }

    const leftMicEl = useMemo(() => {
        if (!atrack) {
            return (
                <div>
                    <MicrophoneIcon style={{ fontSize: '1.3rem' }} />
                </div>
            )
        }
        return (
            <div className="flex gap-1 items-center my-[-2px]">
                <VolumeMeter stream={atrack} variant="pills" channelCount={1} height={24} />
                <MicrophoneIcon style={{ fontSize: '1.3rem' }} />
            </div>
        )
    }, [atrack])

    return (
        <div className={cn('flex gap-2 w-full', props.className)}>
            <SimpleSelect
                items={audioInputs.map((inp) => ({ value: inp.deviceId, label: inp.label }))}
                value={
                    userState.mediaSelection.audioInputs.find(
                        (inp) => inp.deviceId === userState.mediaSelection.talkbackMic
                    )?.deviceId ?? ''
                }
                placeholder="Select Mic"
                className="w-full"
                onChange={handleTalkbackMicChange}
                leftElement={leftMicEl}
                deselectable
                widthMatching
            />
            {props.onOpenSettings && (
                <ControlButton variant="glow" onClick={() => props.onOpenSettings?.()}>
                    <GearIcon />
                </ControlButton>
            )}
        </div>
    )
}
